import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, MapPin, Camera, CheckCircle2, Upload, Youtube, Trash2, FileText, Plus } from 'lucide-react';

const rp = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

export default function AdminBuddhistPage() {
  const { API, token, authHeaders } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState('meets'); // meets | mastery
  const [meets, setMeets] = useState([]);
  const [resources, setResources] = useState([]);
  const [ytOpen, setYtOpen] = useState(false);
  const [editMeet, setEditMeet] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        axios.get(`${API}/admin/buddhist/meets`, authHeaders()),
        axios.get(`${API}/buddhist/mastery/resources`, authHeaders()),
      ]);
      setMeets(m.data.meets || []);
      setResources(r.data.resources || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  }, [API, authHeaders]);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    if (!window.confirm(lang === 'en' ? 'Mark this incentive as paid?' : 'Tandai insentif sudah dibayar?')) return;
    try { await axios.post(`${API}/admin/buddhist/meets/${id}/mark-paid`, {}, authHeaders()); toast.success(lang === 'en' ? 'Marked as paid' : 'Ditandai dibayar'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteMeet = async (id, title) => {
    if (!window.confirm(lang === 'en'
      ? `Delete event "${title}" permanently? This will also delete the event chat group and messages.`
      : `Hapus acara "${title}" secara permanen? Grup chat & pesan acara juga akan dihapus.`)) return;
    try {
      await axios.delete(`${API}/admin/buddhist/meets/${id}`, authHeaders());
      toast.success(lang === 'en' ? 'Event deleted' : 'Acara dihapus');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadResource = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const title = window.prompt(lang === 'en' ? 'Title:' : 'Judul:', file.name);
    if (!title) return;
    const description = window.prompt(lang === 'en' ? 'Description (optional):' : 'Deskripsi (opsional):', '') || '';
    const tags = window.prompt(lang === 'en' ? 'Tags (comma-separated):' : 'Tag (pisah koma):', '') || '';
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('title', title); fd.append('description', description); fd.append('tags', tags);
      await axios.post(`${API}/admin/buddhist/mastery/upload`, fd, { headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(lang === 'en' ? 'Uploaded' : 'Terunggah'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm(lang === 'en' ? 'Delete this resource?' : 'Hapus materi ini?')) return;
    try { await axios.delete(`${API}/admin/buddhist/mastery/${id}`, authHeaders()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen t-bg">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 text-sm t-text-muted hover:text-[#D98A94] mb-4" data-testid="admin-buddhist-back"><ArrowLeft className="w-4 h-4" />{lang === 'en' ? 'Back to Dashboard' : 'Kembali ke Dasbor'}</button>
        <h1 className="text-2xl font-bold t-text mb-4 font-romantic">Buddhist Suite — Admin</h1>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab('meets')} data-testid="admin-buddhist-tab-meets" className={`px-4 py-2 rounded-full text-sm ${tab === 'meets' ? 'bg-[#C7867A] text-white' : 'glass-card t-text-secondary'}`}>{lang === 'en' ? 'Meet Verification' : 'Verifikasi Meet'}</button>
          <button onClick={() => setTab('mastery')} data-testid="admin-buddhist-tab-mastery" className={`px-4 py-2 rounded-full text-sm ${tab === 'mastery' ? 'bg-[#C7867A] text-white' : 'glass-card t-text-secondary'}`}>{lang === 'en' ? 'Mastery Library' : 'Pustaka Mastery'}</button>
        </div>

        {tab === 'meets' && (
          <div className="grid sm:grid-cols-2 gap-3">
            {meets.length === 0 && <p className="t-text-muted col-span-2 text-center py-10">{lang === 'en' ? 'No events yet' : 'Belum ada acara'}</p>}
            {meets.map(m => {
              const statusColor = m.status === 'paid' ? '#16A34A' : m.status === 'completed' ? '#F59E0B' : '#6B7280';
              const proofUrl = m.proof_photo_path ? `${API}/buddhist/files/${m.proof_photo_path}?auth=${encodeURIComponent(token)}` : null;
              return (
                <div key={m.id} className="glass-card rounded-2xl p-4" data-testid={`admin-meet-${m.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div><h3 className="font-semibold t-text text-sm">{m.title}</h3><div className="text-xs t-text-muted">{m.initiator_name} · {m.initiator_whatsapp}</div></div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${statusColor}20`, color: statusColor }}>{m.status}</span>
                  </div>
                  <div className="text-xs t-text-secondary space-y-1 mb-2">
                    <div><Calendar className="w-3 h-3 inline mr-1" />{m.event_date}</div>
                    <div><MapPin className="w-3 h-3 inline mr-1" />{m.location}</div>
                    <div>👥 {m.participants?.length || 0} / {m.max_participants} · {m.event_category}</div>
                  </div>
                  {(m.bank_name || m.bank_account_number) && (
                    <div className="rounded-lg p-2.5 mb-2 text-[11px]" style={{ background: 'rgba(217,138,148,0.08)', border: '1px solid rgba(217,138,148,0.25)' }} data-testid={`admin-meet-bank-${m.id}`}>
                      <div className="font-bold text-[10px] uppercase tracking-wide text-[#D98A94] mb-1">
                        {lang === 'en' ? 'Payout Bank Account' : 'Rekening Bank Pencairan'}
                      </div>
                      <div className="grid grid-cols-3 gap-x-2 gap-y-0.5">
                        <span className="t-text-muted">{lang === 'en' ? 'Bank' : 'Bank'}</span>
                        <span className="col-span-2 t-text font-semibold">{m.bank_name || '-'}</span>
                        <span className="t-text-muted">{lang === 'en' ? 'Holder' : 'a.n.'}</span>
                        <span className="col-span-2 t-text font-semibold">{m.bank_account_holder || '-'}</span>
                        <span className="t-text-muted">{lang === 'en' ? 'No.' : 'No.'}</span>
                        <span className="col-span-2 t-text font-bold font-mono select-all">{m.bank_account_number || '-'}</span>
                      </div>
                    </div>
                  )}
                  {proofUrl && (
                    <div className="mb-2"><a href={proofUrl} target="_blank" rel="noreferrer"><img src={proofUrl} alt="Proof" className="w-full rounded-lg max-h-48 object-cover" /></a><div className="text-[10px] t-text-muted mt-1">📸 {lang === 'en' ? 'Attendees at proof' : 'Peserta saat bukti'}: {m.proof_participant_count} · {rp(m.incentive_amount)}</div></div>
                  )}
                  {m.status === 'completed' && !m.incentive_paid && (
                    <Button onClick={() => markPaid(m.id)} data-testid={`admin-mark-paid-${m.id}`} className="w-full bg-green-600 hover:bg-green-700 text-white text-xs rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{lang === 'en' ? 'Mark Incentive Paid' : 'Tandai Insentif Dibayar'}
                    </Button>
                  )}
                  {m.incentive_paid && <div className="text-center text-xs text-green-600">✓ {lang === 'en' ? 'Paid on' : 'Dibayar'} {new Date(m.incentive_paid_at).toLocaleDateString('id-ID')}</div>}
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => setEditMeet(m)} data-testid={`admin-meet-edit-${m.id}`} variant="outline" className="flex-1 h-8 rounded-full text-xs border-[#D98A94]/40 text-[#D98A94] hover:bg-[#D98A94]/10">
                      ✎ {lang === 'en' ? 'Edit' : 'Edit'}
                    </Button>
                    <Button onClick={() => deleteMeet(m.id, m.title)} data-testid={`admin-meet-delete-${m.id}`} variant="outline" className="flex-1 h-8 rounded-full text-xs border-red-500/40 text-red-600 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3 mr-1" />{lang === 'en' ? 'Delete' : 'Hapus'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'mastery' && (
          <MasteryLibrary
            API={API}
            token={token}
            resources={resources}
            authHeaders={authHeaders}
            lang={lang}
            onUpload={uploadResource}
            onAddYt={() => setYtOpen(true)}
            onDelete={deleteResource}
          />
        )}

        {ytOpen && <YoutubeAddModal API={API} authHeaders={authHeaders} lang={lang} onClose={() => setYtOpen(false)} onAdded={() => { setYtOpen(false); load(); }} />}

        {editMeet && (
          <AdminMeetEditModal
            API={API}
            authHeaders={authHeaders}
            lang={lang}
            meet={editMeet}
            onClose={() => setEditMeet(null)}
            onSaved={() => { setEditMeet(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function AdminMeetEditModal({ API, authHeaders, lang, meet, onClose, onSaved }) {
  const isID = lang !== 'en';
  const [f, setF] = useState({
    title: meet.title || '',
    description: meet.description || '',
    location: meet.location || '',
    maps_url: meet.maps_url || '',
    event_date: meet.event_date || '',
    event_time_start: meet.event_time_start || '',
    event_time_end: meet.event_time_end || '',
    event_category: meet.event_category || 'Kebaktian',
    max_participants: meet.max_participants || 50,
    bank_name: meet.bank_name || '',
    bank_account_holder: meet.bank_account_holder || '',
    bank_account_number: meet.bank_account_number || '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title.trim() || !f.location.trim() || !f.event_date) {
      return toast.error(isID ? 'Judul, lokasi & tanggal wajib' : 'Title, location & date required');
    }
    if (f.event_time_start && f.event_time_end && f.event_time_end <= f.event_time_start) {
      return toast.error(isID ? 'Jam selesai harus setelah jam mulai' : 'End time must be after start time');
    }
    setBusy(true);
    try {
      await axios.put(`${API}/admin/buddhist/meets/${meet.id}`, {
        ...f,
        max_participants: Number(f.max_participants) || 50,
      }, authHeaders());
      toast.success(isID ? 'Acara diperbarui' : 'Event updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass-card t-text max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-meet-edit-modal">
        <DialogHeader><DialogTitle>{isID ? 'Edit Acara (Admin)' : 'Edit Event (Admin)'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder={isID ? 'Judul *' : 'Title *'} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="admin-meet-edit-title" />
          <Textarea placeholder={isID ? 'Deskripsi' : 'Description'} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="admin-meet-edit-desc" />
          <Input placeholder={isID ? 'Lokasi *' : 'Location *'} value={f.location} onChange={e => setF({ ...f, location: e.target.value })} data-testid="admin-meet-edit-location" />
          <Input placeholder={isID ? 'Google Maps URL (opsional)' : 'Google Maps URL (optional)'} value={f.maps_url} onChange={e => setF({ ...f, maps_url: e.target.value })} data-testid="admin-meet-edit-maps" />
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Tanggal Acara' : 'Event Date'} *</label>
            <Input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} data-testid="admin-meet-edit-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Jam Mulai' : 'Start Time'}</label>
              <Input type="time" value={f.event_time_start} onChange={e => setF({ ...f, event_time_start: e.target.value })} data-testid="admin-meet-edit-time-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Jam Selesai' : 'End Time'}</label>
              <Input type="time" value={f.event_time_end} onChange={e => setF({ ...f, event_time_end: e.target.value })} data-testid="admin-meet-edit-time-end" />
            </div>
          </div>
          <Input placeholder={isID ? 'Kategori acara' : 'Event category'} value={f.event_category} onChange={e => setF({ ...f, event_category: e.target.value })} />
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Maks Peserta' : 'Max Participants'}</label>
            <Input type="number" value={f.max_participants} onChange={e => setF({ ...f, max_participants: e.target.value })} data-testid="admin-meet-edit-maxparts" />
          </div>
          <div className="rounded-lg p-2.5 space-y-2" style={{ background: 'rgba(217,138,148,0.08)', border: '1px solid rgba(217,138,148,0.25)' }}>
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#D98A94]">
              {isID ? 'Rekening Bank Pencairan Insentif' : 'Incentive Payout Bank'}
            </div>
            <Input placeholder={isID ? 'Nama bank' : 'Bank name'} value={f.bank_name} onChange={e => setF({ ...f, bank_name: e.target.value })} />
            <Input placeholder={isID ? 'Nama pemilik rekening' : 'Account holder name'} value={f.bank_account_holder} onChange={e => setF({ ...f, bank_account_holder: e.target.value })} />
            <Input placeholder={isID ? 'Nomor rekening' : 'Account number'} value={f.bank_account_number} onChange={e => setF({ ...f, bank_account_number: e.target.value })} />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{isID ? 'Batal' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={busy} className="flex-1 bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-full" data-testid="admin-meet-edit-submit">
            {busy ? '...' : (isID ? 'Simpan Perubahan' : 'Save Changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function YoutubeAddModal({ API, authHeaders, lang, onClose, onAdded }) {
  const [f, setF] = useState({ title: '', description: '', youtube_url: '', tags: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.title || !f.youtube_url) return toast.error(lang === 'en' ? 'Title and URL required' : 'Judul & URL wajib');
    setLoading(true);
    try { await axios.post(`${API}/admin/buddhist/mastery/youtube`, f, authHeaders()); toast.success(lang === 'en' ? 'Added' : 'Ditambahkan'); onAdded(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-md">
        <DialogHeader><DialogTitle>{lang === 'en' ? 'Add YouTube Video' : 'Tambah Video YouTube'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder={lang === 'en' ? 'Title *' : 'Judul *'} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="yt-input-title" />
          <Input placeholder="https://youtube.com/watch?v=..." value={f.youtube_url} onChange={e => setF({ ...f, youtube_url: e.target.value })} data-testid="yt-input-url" />
          <Textarea placeholder={lang === 'en' ? 'Description' : 'Deskripsi'} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} />
          <Input placeholder={lang === 'en' ? 'Tags (comma-separated)' : 'Tag (pisah koma)'} value={f.tags} onChange={e => setF({ ...f, tags: e.target.value })} />
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{lang === 'en' ? 'Cancel' : 'Batal'}</Button>
          <Button onClick={submit} disabled={loading} className="flex-1 bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-full" data-testid="yt-submit-btn">{loading ? '...' : (lang === 'en' ? 'Add' : 'Tambah')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────
// Admin Mastery library — search + inline PDF/YouTube preview
// ─────────────────────────────────────────────────────────────
function MasteryLibrary({ API, token, resources, lang, onUpload, onAddYt, onDelete }) {
  const [query, setQuery] = useState('');
  const isID = lang !== 'en';
  const q = query.trim().toLowerCase();
  const filtered = q
    ? resources.filter(r => {
        const hay = [r.title, r.description, r.uploader_name, r.original_filename, (r.tags || []).join(' ')]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : resources;
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <label className="flex-1">
          <input type="file" onChange={onUpload} className="hidden" data-testid="admin-mastery-upload-input" />
          <div className="cursor-pointer bg-[#C7867A] hover:bg-[#C7867A]/90 text-white text-sm font-semibold py-2.5 rounded-full text-center">
            <Upload className="w-4 h-4 inline mr-1" />{isID ? 'Upload File' : 'Upload File'}
          </div>
        </label>
        <Button onClick={onAddYt} data-testid="admin-mastery-add-yt" className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full">
          <Youtube className="w-4 h-4 mr-1" />{isID ? 'Tambah YouTube' : 'Add YouTube'}
        </Button>
      </div>
      <div className="relative mb-3">
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid="admin-mastery-search"
          placeholder={isID ? 'Cari materi… (judul, deskripsi, tag, uploader)' : 'Search library… (title, desc, tag, uploader)'}
          className="w-full h-10 pl-9 pr-3 rounded-full glass-card t-text placeholder:t-text-muted text-sm border-0 outline-none focus:ring-2 focus:ring-[#D98A94]/40"
        />
        <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 t-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>
        {q && (
          <div className="text-[10px] t-text-muted mt-1 ml-2">
            {isID ? `${filtered.length} hasil dari ${resources.length}` : `${filtered.length} of ${resources.length}`}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.length === 0 && (
          <p className="t-text-muted col-span-2 text-center py-10">
            {q ? (isID ? 'Tidak ada hasil' : 'No results') : (isID ? 'Belum ada materi' : 'No resources')}
          </p>
        )}
        {filtered.map(r => (
          <AdminMasteryCard key={r.id} resource={r} token={token} API={API} lang={lang} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function AdminMasteryCard({ resource: r, token, API, lang, onDelete }) {
  const isID = lang !== 'en';
  const ext = (r.original_filename || '').split('.').pop().toLowerCase();
  const ct = r.content_type || '';
  const isPdf = ct === 'application/pdf' || ext === 'pdf';
  const isPpt = ['ppt', 'pptx'].includes(ext) || ct.includes('powerpoint') || ct.includes('presentation');
  const isYt = r.resource_type === 'youtube';
  const url = !isYt ? `${API}/buddhist/files/${r.storage_path}?auth=${encodeURIComponent(token)}` : null;
  const officeViewerUrl = url ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}` : null;
  const meta = [r.uploader_name, r.created_at && new Date(r.created_at).toLocaleString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })]
    .filter(Boolean).join(' · ');
  return (
    <div className="glass-card rounded-2xl overflow-hidden relative" data-testid={`admin-resource-${r.id}`}>
      <button
        onClick={() => onDelete(r.id)}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 hover:bg-red-50 shadow-sm"
        data-testid={`admin-resource-delete-${r.id}`}
        title={isID ? 'Hapus' : 'Delete'}
      >
        <Trash2 className="w-3.5 h-3.5 text-red-500" />
      </button>
      {isYt && (
        <div className="aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${r.youtube_id}`}
            title={r.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`admin-resource-yt-iframe-${r.id}`}
          />
        </div>
      )}
      {isPdf && (
        <div className="bg-gray-100" style={{ height: 320 }}>
          <iframe
            src={`${url}#toolbar=1&navpanes=0&view=FitH`}
            title={r.title}
            className="w-full h-full border-0"
            data-testid={`admin-resource-pdf-iframe-${r.id}`}
          />
        </div>
      )}
      {isPpt && officeViewerUrl && (
        <div className="bg-gray-100 relative" style={{ height: 320 }}>
          <iframe
            src={officeViewerUrl}
            title={r.title}
            className="w-full h-full border-0"
            data-testid={`admin-resource-ppt-iframe-${r.id}`}
            allow="fullscreen"
          />
          <div className="absolute bottom-1 right-2 text-[9px] t-text-muted italic bg-white/70 px-1.5 rounded">
            Microsoft Office Online
          </div>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-1 mb-1">
          {isYt ? <Youtube className="w-3.5 h-3.5 text-red-500" /> : <FileText className="w-3.5 h-3.5 text-[#D98A94]" />}
          <span className="text-[10px] t-text-muted uppercase">{isYt ? 'YouTube' : (ext || 'File')}</span>
        </div>
        <h3 className="font-semibold t-text text-sm leading-tight">{r.title}</h3>
        {r.description && <p className="text-xs t-text-secondary mt-1 line-clamp-3">{r.description}</p>}
        {meta && (
          <p className="text-[10px] t-text-muted mt-2 italic">
            {isID ? 'Ditambahkan oleh' : 'Added by'}: {meta}
          </p>
        )}
      </div>
    </div>
  );
}
